import { useEffect, useState, useCallback } from "react";
import { useConfirm } from "./ConfirmDialog";

// ── API helper ────────────────────────────────────────────────────────────────
async function apiFetch(path, opts = {}) {
  const token = localStorage.getItem("token");
  const res = await fetch(`/v1/fw${path}`, {
    method: opts.method || "GET",
    headers: {
      "Authorization": `Bearer ${token || ""}`,
      "Content-Type": "application/json",
    },
    body: opts.body || undefined,
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail || `HTTP ${res.status}`);
  }
  return res.status === 204 ? null : res.json();
}

const C = {
  bg:      "var(--bg,#0f172a)",
  surface: "var(--surface,#1e293b)",
  border:  "var(--border,#334155)",
  text:    "var(--text,#f1f5f9)",
  muted:   "var(--muted,#94a3b8)",
  accent:  "var(--accent,#38bdf8)",
  green:   "#22c55e",
  red:     "#ef4444",
  yellow:  "#f59e0b",
};

function Badge({ children, color }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4,
      background: `${color}22`, color, letterSpacing: ".4px",
      textTransform: "uppercase", border: `1px solid ${color}44`,
    }}>{children}</span>
  );
}

// list = "whitelist" | "blacklist"
export default function FirewallLists({ list, onChanged }) {
  const isWhite = list === "whitelist";
  const accent  = isWhite ? C.green : C.red;

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [ip,      setIp]      = useState("");
  const [comment, setComment] = useState("");
  const [busy,    setBusy]    = useState(false);
  const [removing,setRemoving]= useState("");
  // window.confirm no abre cartel en el navegador de Carlos y devuelve false: cancelaba solo.
  const [confirmar, dialogoConfirm] = useConfirm();

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const d = await apiFetch(`/${list}`);
      setEntries(d.entries || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [list]);

  useEffect(() => { load(); }, [load]);

  async function handleAdd() {
    if (!ip.trim()) return;
    setBusy(true); setError("");
    try {
      await apiFetch(`/${list}`, {
        method: "POST",
        body: JSON.stringify({ ip: ip.trim(), comment: comment.trim() || null }),
      });
      setIp(""); setComment("");
      await load(); onChanged && onChanged();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function handleRemove(entryIp) {
    if (!(await confirmar(`¿Quitar ${entryIp} de la ${list}?`))) return;
    setRemoving(entryIp); setError("");
    try {
      await apiFetch(`/${list}/${entryIp}`, { method: "DELETE" });
      await load(); onChanged && onChanged();
    } catch (e) { setError(e.message); }
    finally { setRemoving(""); }
  }

  const inputStyle = {
    padding: "8px 10px", borderRadius: 7, border: `1px solid ${C.border}`,
    background: C.bg, color: C.text, fontSize: 13, outline: "none",
  };

  return (
    <div>
      {dialogoConfirm}
      {/* Encabezado */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>
          {isWhite ? "✅ Whitelist (bypass total)" : "⛔ Blacklist (bloqueo)"}
        </span>
        <Badge color={accent}>{entries.length} IPs</Badge>
      </div>

      <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>
        {isWhite
          ? "Las IPs en whitelist se aceptan siempre: saltan el geo-filtro y nunca se banean."
          : "Las IPs en blacklist se bloquean (DROP). Los bloqueos automáticos de fail2ban aterrizan acá de forma permanente."}
      </div>

      {/* Alta */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <input
          value={ip} onChange={e => setIp(e.target.value)}
          placeholder="IP o CIDR (ej: 181.94.203.166 | 200.93.0.0/16)"
          style={{ ...inputStyle, flex: "1 1 240px" }}
          onKeyDown={e => e.key === "Enter" && handleAdd()}
        />
        <input
          value={comment} onChange={e => setComment(e.target.value)}
          placeholder="Comentario (opcional)"
          style={{ ...inputStyle, flex: "1 1 200px" }}
          onKeyDown={e => e.key === "Enter" && handleAdd()}
        />
        <button onClick={handleAdd} disabled={busy || !ip.trim()} style={{
          padding: "8px 18px", borderRadius: 8, border: "none",
          background: accent, color: "#0f172a", fontWeight: 700,
          fontSize: 13, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? .7 : 1,
        }}>
          {busy ? "Agregando…" : "+ Agregar"}
        </button>
      </div>

      {error && (
        <div style={{
          marginBottom: 14, padding: "10px 14px", borderRadius: 8,
          background: `${C.red}15`, border: `1px solid ${C.red}44`,
          color: C.red, fontSize: 12, fontFamily: "monospace",
        }}>{error}</div>
      )}

      {/* Tabla */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "28px 0", color: C.muted }}>Cargando…</div>
      ) : entries.length === 0 ? (
        <div style={{ textAlign: "center", padding: "28px 0", color: C.muted, fontSize: 13 }}>
          Sin entradas en la {list}.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {["IP / CIDR", "Comentario", "Origen", ""].map(h => (
                  <th key={h} style={{
                    padding: "8px 12px", textAlign: "left", fontSize: 11,
                    color: C.muted, fontWeight: 600, textTransform: "uppercase",
                    letterSpacing: ".4px",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.ip} style={{ borderBottom: `1px solid ${C.border}22` }}>
                  <td style={{ padding: "10px 12px", color: C.text, fontFamily: "monospace" }}>{e.ip}</td>
                  <td style={{ padding: "10px 12px", color: C.muted, fontSize: 12 }}>{e.comment || "—"}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <Badge color={e.source === "auto" ? C.yellow : C.muted}>
                      {e.source === "auto" ? "auto" : "manual"}
                    </Badge>
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right" }}>
                    <button
                      onClick={() => handleRemove(e.ip)}
                      disabled={removing === e.ip}
                      title={`Quitar ${e.ip}`}
                      style={{
                        background: "none", border: `1px solid ${C.red}55`,
                        color: C.red, borderRadius: 6, padding: "4px 10px",
                        fontSize: 12, cursor: removing === e.ip ? "not-allowed" : "pointer",
                        opacity: removing === e.ip ? .5 : 1,
                      }}>
                      {removing === e.ip ? "…" : "🗑 Quitar"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
